import React, { useCallback, useMemo, useState } from 'react';
import { View, StyleSheet, ScrollView, useWindowDimensions, Platform, Linking, Switch } from 'react-native';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { RADIUS } from '@/constants/shared/theme';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { DESKTOP_CONFIRM_COPY, FUNDING_PATH } from '@/constants/admin';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { AdminNavList } from '@/components/admin/nav/AdminNavList';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import LinearGradient from 'react-native-linear-gradient';
import { useAdminScreen } from '@/hooks/admin/useAdminScreen';
import { ResioLogo } from '@/assets/ResioLogo';
import { SVG_COMPONENTS, SVG_DIMENSIONS } from '@/assets/admin';
import { PropertyOrgSelectors } from '@/components/admin/nav/PropertyOrgSelectors';
import { useAdminNavigation } from '@/hooks/admin/useAdminNavigation';
import { useDisplaySelectors } from '@/hooks/admin/useDisplaySelectors';
import { useAppDispatch, useAppSelector } from '@/store';
import { setSelectedOrganizationId, setSelectedPropertyId, toggleSidebarCollapsed } from '@/store/slices/admin/adminUISlice';
import { selectIsSidebarCollapsed } from '@/store/selectors/adminUISelectors';
import { WebConfirmModal } from '@/components/common/modals';
import { getScreenMetadata } from '@/utils/admin/navigation';

interface AdminSidebarProps {
  routeName: keyof AdminStackParamList;
  onNavigate: (path: string) => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = React.memo(({ routeName, onNavigate }) => {
  const { admin, redesign } = useAdminColors();
  const { mode, toggleTheme, components } = useThemeColors();
  const space = useSpacing();
  const isDarkMode = mode === 'dark';
  const { handleSignOut } = useAdminScreen(routeName);
  const currentPath = useMemo(() => getScreenMetadata(routeName).path, [routeName]);
  const { adminUser } = useAdminNavigation();
  const displaySelectors = useDisplaySelectors(currentPath);
  const dispatch = useAppDispatch();
  const isFunding = useMemo(() => currentPath.includes(FUNDING_PATH), [currentPath]);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const isCollapsed = useAppSelector(selectIsSidebarCollapsed);

  const handleToggleCollapse = useCallback(() => {
    dispatch(toggleSidebarCollapsed());
  }, [dispatch]);

  const handleOrganizationChange = useCallback((orgId: number) => {
    dispatch(setSelectedOrganizationId(orgId));
    const availableProperty = adminUser.properties?.find(prop => prop.organizationId === orgId);
    if (availableProperty) {
      dispatch(setSelectedPropertyId(availableProperty.id));
    }
  }, [dispatch, adminUser.properties]);

  const handlePropertyChange = useCallback((propId: number) => {
    dispatch(setSelectedPropertyId(propId));
  }, [dispatch]);

  const handleSignOutClick = useCallback(() => {
    setShowSignOutModal(true);
  }, []);

  const handleConfirmSignOut = useCallback(() => {
    setShowSignOutModal(false);
    handleSignOut();
  }, [handleSignOut]);

  const handleCancelSignOut = useCallback(() => {
    setShowSignOutModal(false);
  }, []);

  const { height: windowHeight } = useWindowDimensions();
  const containerHeight = Platform.OS === 'web' ? { height: windowHeight } : undefined;

  const headerGlowColors = useMemo(
    () => admin.sidebar.headerGlow.map((s) => s.color),
    [admin.sidebar.headerGlow],
  );
  const headerGlowLocations = useMemo(
    () => admin.sidebar.headerGlow.map((s) => s.offset),
    [admin.sidebar.headerGlow],
  );

  return (
    <View style={[styles.container, containerHeight]}>
      <View
        style={[
          styles.inner,
          { backgroundColor: admin.sidebar.bg, borderRightColor: admin.sidebar.border },
        ]}
      >
        {/* Header glow — token-driven blue → purple → red wash that fades by ~180px. */}
        <LinearGradient
          colors={headerGlowColors}
          locations={headerGlowLocations}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={[styles.headerGlow, { height: admin.sidebar.headerGlowHeight }]}
          pointerEvents="none"
        />
        <View style={[styles.header, isCollapsed && styles.headerCollapsed]}>
          <Hoverable hover="row"
            bleed={{ x: space.sm, y: space.sm }}
            onPress={() => onNavigate('/residents')}
            style={styles.logoContainer}
          >
            <ResioLogo width={40} height={40} />
            {!isCollapsed ? (
              <Text style={[styles.logoText, { color: admin.sidebar.label }]}>RESIO</Text>
            ) : null}
          </Hoverable>
          {!isCollapsed ? (
            <Hoverable hover="glass"
              onPress={handleToggleCollapse}
              style={[styles.collapseButton, { borderColor: admin.sidebar.border }]}
              accessibilityRole={'button' as any}
              accessibilityLabel="Collapse sidebar"
            >
              <SVG_COMPONENTS.CHEVRON_LEFT
                {...SVG_DIMENSIONS.CHEVRON_LEFT}
                color={admin.sidebar.label}
              />
            </Hoverable>
          ) : null}
        </View>
        {isCollapsed ? (
          <Hoverable hover="glass"
            onPress={handleToggleCollapse}
            style={[styles.collapseButtonRail, { borderColor: admin.sidebar.border }]}
            accessibilityRole={'button' as any}
            accessibilityLabel="Expand sidebar"
          >
            <SVG_COMPONENTS.CHEVRON_RIGHT
              {...SVG_DIMENSIONS.CHEVRON_RIGHT}
              color={admin.sidebar.label}
            />
          </Hoverable>
        ) : null}
        {!isCollapsed ? (
          <View style={styles.orgPropSection}>
            <PropertyOrgSelectors
              organizations={adminUser.organizations || []}
              properties={adminUser.properties || []}
              selectedOrganizationId={adminUser.selectedOrganizationId}
              selectedPropertyId={adminUser.selectedPropertyId}
              onOrganizationChange={handleOrganizationChange}
              onPropertyChange={handlePropertyChange}
              showPropertySelector={!isFunding && displaySelectors.includes('property')}
              disableOrganization={!displaySelectors.includes('organization')}
              disabled={false}
            />
          </View>
        ) : null}
        <ScrollView
          style={styles.navContainer}
          contentContainerStyle={[
            styles.navScrollContent,
            isCollapsed && styles.navScrollContentCollapsed,
          ]}
          showsVerticalScrollIndicator={false}
        >
          <AdminNavList currentPath={currentPath} onNavigate={onNavigate} />
        </ScrollView>
        <View style={[styles.footerContainer, isCollapsed && styles.footerContainerCollapsed]}>
          {!isCollapsed ? (
            <View style={[styles.themeToggleRow, { borderBottomColor: admin.sidebar.border }]}>
              <Text style={[styles.themeToggleLabel, { color: admin.sidebar.label }]}>Dark mode</Text>
              <Switch
                value={isDarkMode}
                onValueChange={toggleTheme}
                trackColor={{ false: admin.sidebar.border, true: PRIMITIVE_COLORS.resioBlue }}
                thumbColor={components.toggle.thumb}
                accessibilityLabel="Toggle dark mode"
              />
            </View>
          ) : null}
          {!isCollapsed ? (
            <Hoverable
              hover="link"
              onPress={() => Linking.openURL('https://www.resio.com/')}
              accessibilityRole={'link' as any}
              accessibilityLabel="Learn more about Resio"
            >
              {({ textStyle }) => (
                <Text style={[styles.learnMoreText, { color: admin.sidebar.label }]}>
                  Learn more about us at{"\n"}
                  {/* Only the link-coloured span takes the hover ink; the grey caption keeps its colour. */}
                  <Text style={[styles.learnMoreHighlight, { color: admin.sidebar.indicator }, textStyle]}>RESIO.COM</Text>
                </Text>
              )}
            </Hoverable>
          ) : null}
          {isCollapsed ? (
            <Hoverable hover="row"
              onPress={toggleTheme}
              style={styles.themeToggleIconButton}
              accessibilityRole={'button' as any}
              accessibilityLabel={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <Text style={[styles.themeToggleIconGlyph, { color: admin.sidebar.label }]}>
                {isDarkMode ? '☀︎' : '🌙︎'}
              </Text>
            </Hoverable>
          ) : null}
          <Hoverable hover="row"
            onPress={handleSignOutClick}
            style={[
              styles.logoutButton,
              isCollapsed && styles.logoutButtonCollapsed,
              { borderColor: redesign.accent.red },
            ]}
            accessibilityRole={'button' as any}
            accessibilityLabel="Sign Out"
          >
            {isCollapsed ? (
              <Text
                style={[styles.logoutButtonIcon, { color: redesign.accent.red }]}
                accessibilityLabel="Sign Out"
              >
                ⏻
              </Text>
            ) : (
              <Text style={[styles.logoutButtonText, { color: redesign.accent.red }]}>Sign Out</Text>
            )}
          </Hoverable>
        </View>
      </View>
      <WebConfirmModal
        visible={showSignOutModal}
        title={DESKTOP_CONFIRM_COPY.signOut.title}
        message={DESKTOP_CONFIRM_COPY.signOut.message}
        confirmText={DESKTOP_CONFIRM_COPY.signOut.confirmText}
        cancelText={DESKTOP_CONFIRM_COPY.signOut.cancelText}
        onConfirm={handleConfirmSignOut}
        onCancel={handleCancelSignOut}
      />
    </View>
  );
});
AdminSidebar.displayName = 'AdminSidebar';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
    position: 'relative',
    // height assigned dynamically for web via useWindowDimensions
    overflow: 'hidden',
  },
  inner: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    borderRightWidth: 1,
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerCollapsed: {
    paddingHorizontal: 12,
    justifyContent: 'center',
  },
  logoContainer: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  collapseButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  collapseButtonRail: {
    alignSelf: 'center',
    width: 28,
    height: 28,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
    marginBottom: 8,
  },
  orgPropSection: {
    width: '100%',
    paddingTop: 12,
    paddingRight: 20,
    paddingBottom: 10,
    paddingLeft: 20,
  },
  navContainer: {
    flex: 1,
  },
  navScrollContent: {
    flexGrow: 1,
    padding: 16,
  },
  navScrollContentCollapsed: {
    paddingHorizontal: 8,
  },
  footerContainer: {
    paddingTop: 16,
    paddingHorizontal: 20,
    paddingBottom: 20,
    gap: 12,
  },
  footerContainerCollapsed: {
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  headerGlow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 0,
  },
  logoText: {
    fontSize: 30,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  learnMoreText: {
    fontSize: 14,
    lineHeight: 18,
    fontWeight: '600',
  },
  learnMoreHighlight: {},
  themeToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  themeToggleLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  logoutButton: {
    alignSelf: 'stretch',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoutButtonCollapsed: {
    alignSelf: 'center',
    width: 40,
    height: 40,
    paddingVertical: 0,
  },
  logoutButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  logoutButtonIcon: {
    fontSize: 20,
    fontWeight: '600',
    lineHeight: 22,
  },
  themeToggleIconButton: {
    alignSelf: 'center',
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    // The nav items' radius, so the hover tint is a rounded plate.
    borderRadius: RADIUS.md,
  },
  themeToggleIconGlyph: {
    fontSize: 20,
    fontWeight: '600',
    lineHeight: 22,
  },
});
